import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Loader2,
  BookMarked,
  MapPinned,
  GraduationCap,
  CalendarClock,
  Wallet,
  MessageSquare,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import CmsSwitcherBar from "@/components/app/CmsSwitcherBar";
import OfferRateCoverageCard from "@/components/admin/OfferRateCoverageCard";
import ProfessorRegenTile from "@/components/admin/ProfessorRegenTile";
import ScrapflyUsageTile from "@/components/admin/ScrapflyUsageTile";
import ReviewsBackfillTile from "@/components/admin/ReviewsBackfillTile";
import TopUnisCoverageTile from "@/components/admin/TopUnisCoverageTile";
import URLRotWatchdogTile from "@/components/admin/URLRotWatchdogTile";
import NarratorDaemonTile from "@/components/admin/NarratorDaemonTile";
import NightlyHarvestTile from "@/components/admin/NightlyHarvestTile";
import CourseGrowthTile from "@/components/admin/CourseGrowthTile";
import CoverageSnapshotTile from "@/components/admin/CoverageSnapshotTile";
import BurnRateTile from "@/components/admin/BurnRateTile";
import CoverageAssessmentCard from "@/components/admin/CoverageAssessmentCard";
import UniWideCoverageCard from "@/components/admin/UniWideCoverageCard";
import MilestoneBanner from "@/components/admin/MilestoneBanner";
import TokenBudgetStrip from "@/components/admin/TokenBudgetStrip";
import DataGapReportsTile from "@/components/admin/DataGapReportsTile";

const REFRESH_MS = 60_000;

/**
 * /admin/progress — Progress Dash. One home for every backfill,
 * daemon and AI-content coverage tile so we can eyeball what is
 * still filling and what is done without hunting them across the
 * admin surface.
 */
export default function AdminProgressDash() {
  const { user, loading } = useAuth();
  const [checking, setChecking] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [snap, setSnap] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      setChecking(false);
      return;
    }
    (async () => {
      try {
        await api.get("/admin/whoami");
        setIsAdmin(true);
      } catch {
        setIsAdmin(false);
      } finally {
        setChecking(false);
      }
    })();
  }, [user, loading]);

  const loadSnaps = useCallback(async () => {
    try {
      const r = await api.get("/admin/progress/coverage-snapshots");
      setSnap(r.data?.snapshots || null);
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load coverage snapshots");
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    loadSnaps();
    const t = setInterval(loadSnaps, REFRESH_MS);
    return () => clearInterval(t);
  }, [isAdmin, loadSnaps]);

  if (loading || checking) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-24 text-stone-500 font-mono text-sm">
        <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
        Loading Progress Dash…
      </div>
    );
  }
  if (!isAdmin) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16">
        <div className="p-6 border border-stone-300 rounded-lg bg-white text-sm text-stone-700">
          Not authorised. <Link to="/admin" className="underline">Back to Admin Home</Link>.
        </div>
      </div>
    );
  }

  return (
    <>
      <CmsSwitcherBar />
      <div
        className="max-w-6xl mx-auto px-6 py-12"
        data-testid="admin-progress-dash"
      >
        <div className="flex items-baseline justify-between flex-wrap gap-3">
          <div>
            <Link
              to="/admin"
              className="inline-flex items-center gap-1 text-[11px] font-underground-sc tracking-widest text-stone-500 hover:text-stone-900"
            >
              <ArrowLeft className="h-3 w-3" /> Newsroom
            </Link>
            <h1 className="mt-2 font-editorial text-4xl">Progress Dash</h1>
            <p className="mt-2 text-stone-600 text-sm max-w-2xl">
              Every backfill, daemon and AI-content coverage tile in one place. Refreshes every 60 seconds.
            </p>
            <p className="mt-2 text-[12px] text-stone-500 max-w-2xl leading-relaxed">
              <span className="font-medium text-stone-700">How to read the bars:</span>{" "}
              <span className="inline-block align-middle w-3 h-1.5 bg-emerald-500 rounded-full mr-1" />
              <span className="font-medium">of narrated</span> — % of the AI-addressable slice that carries the field.
              <span className="mx-2 text-stone-300">·</span>
              <span className="inline-block align-middle w-3 h-1 bg-stone-500 rounded-full mr-1 opacity-60" />
              <span className="font-medium">of catalogue</span> — the same field's true share of every course in that country.
            </p>
          </div>
        </div>

        <TokenBudgetStrip />

        {/* Weekly milestone banner — celebrates grade transitions and coverage wins */}
        <section className="mt-6">
          <MilestoneBanner />
        </section>

        {/* Honest coverage self-assessment — A→E grade vs peers */}
        <section className="mt-8">
          <CoverageAssessmentCard />
        </section>

        {/* Uni-wide entry-req context coverage — separate grade,
            deliberately isolated from the per-course rubric */}
        <section className="mt-2">
          <UniWideCoverageCard />
        </section>

        {/* Family feedback inbox — surfaces "help us improve" notes */}
        <section className="mt-8">
          <div className="text-[10px] font-underground-sc tracking-widest text-stone-500 mb-3">
            Data editing
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            <Link
              to="/admin/edit"
              className="block border border-stone-200 rounded-xl p-4 bg-white hover:border-purple-300 hover:shadow-sm transition-all"
              data-testid="tile-admin-edit"
            >
              <div className="text-sm font-medium text-stone-900">
                Edit any course or uni
              </div>
              <div className="mt-1 text-[12px] text-stone-500 leading-snug">
                Search + open the registry editor for any degree or
                university.
              </div>
            </Link>
            <Link
              to="/admin/uni-verification"
              className="block border border-stone-200 rounded-xl p-4 bg-white hover:border-amber-300 hover:shadow-sm transition-all"
              data-testid="tile-uni-verification"
            >
              <div className="text-sm font-medium text-stone-900">
                Uni verification leaderboard 🏆
              </div>
              <div className="mt-1 text-[12px] text-stone-500 leading-snug">
                Rank claimed unis by fields verified. Nudge the cold ones.
              </div>
            </Link>
            <DataGapReportsTile />
          </div>
        </section>

        {/* Family feedback inbox — surfaces "help us improve" notes */}
        <section className="mt-8 hidden">
          <div className="text-[10px] font-underground-sc tracking-widest text-stone-500 mb-3">
            Family feedback
          </div>
          <DataGapReportsTile />
        </section>

        {/* AI content coverage — batch/one-off generators */}
        <section className="mt-10">
          <div className="text-[10px] font-underground-sc tracking-widest text-stone-500 mb-3">
            AI content coverage
          </div>
          {err && (
            <div className="mb-4 border border-rose-200 bg-rose-50/50 rounded-xl p-4 text-sm text-rose-900">
              Coverage snapshots failed: {err}
            </div>
          )}
          {!snap && !err && (
            <div className="border border-stone-200 rounded-xl p-4 text-sm text-stone-500">
              <Loader2 className="inline h-4 w-4 animate-spin mr-1" />
              Loading coverage snapshots…
            </div>
          )}
          {snap && (
            <div className="grid grid-cols-1 gap-4">
              <CoverageSnapshotTile
                testId="tile-cluster5-course"
                icon={GraduationCap}
                tone="violet"
                daemonHint="Program: wua-cluster5-generator"
                daemonProgram="wua-cluster5-generator"
                snapshot={snap.cluster5_course_essay}
                onRefresh={loadSnaps}
                recentLabelKey="title"
              />
              <CoverageSnapshotTile
                testId="tile-cluster5-city"
                icon={MapPinned}
                tone="sky"
                daemonHint="Program: wua-cluster5-generator"
                daemonProgram="wua-cluster5-generator"
                snapshot={snap.cluster5_city_essay}
                onRefresh={loadSnaps}
                recentLabelKey="title"
              />
              <CoverageSnapshotTile
                testId="tile-course-tips"
                icon={BookMarked}
                tone="amber"
                daemonHint="Program: wua-course-tips-generator"
                daemonProgram="wua-course-tips-generator"
                snapshot={snap.course_tips}
                onRefresh={loadSnaps}
                recentLabelKey="title"
              />
              <CoverageSnapshotTile
                testId="tile-us-deadlines"
                icon={CalendarClock}
                tone="rose"
                daemonHint="Program: wua-us-deadline-daemon"
                daemonProgram="wua-us-deadline-daemon"
                snapshot={snap.us_deadlines}
                onRefresh={loadSnaps}
                recentLabelKey="title"
              />
              <CoverageSnapshotTile
                testId="tile-scholarships"
                icon={Wallet}
                tone="emerald"
                daemonHint="Program: wua-scholarship-extract (+ US variant)"
                daemonProgram="wua-scholarship-extract"
                snapshot={snap.scholarships_extraction}
                onRefresh={loadSnaps}
                recentLabelKey="name"
              />
            </div>
          )}
        </section>

        {/* Existing daemon + backfill tiles */}
        <section className="mt-10">
          <div className="text-[10px] font-underground-sc tracking-widest text-stone-500 mb-3">
            Daemons &amp; backfills
          </div>
          <div className="space-y-4">
            <NightlyHarvestTile />
            <CourseGrowthTile />
            <BurnRateTile />
            <NarratorDaemonTile />
            <ReviewsBackfillTile />
            <OfferRateCoverageCard />
            <TopUnisCoverageTile />
            <ProfessorRegenTile />
            <ScrapflyUsageTile />
            <URLRotWatchdogTile />
          </div>
        </section>
      </div>
    </>
  );
}
